<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Copy, Download, ExternalLink, FileText, LoaderCircle, Pencil, ReceiptText } from 'lucide-vue-next';
import { formatMoney } from '~/lib/formatters';
import type { Invoice, InvoiceLineItem, Quote, QuoteLineItem } from '~/types/models';
import { quoteWasSharedWithClient, resolveQuoteAcceptanceSource } from '~/lib/quoteLifecycle';
import { toast } from 'vue-sonner';

const props = withDefaults(defineProps<{
  open: boolean;
  kind: 'quote' | 'invoice';
  quote?: Quote | null;
  invoice?: Invoice | null;
  clientName?: string;
  projectName?: string;
  linkedInvoices?: Invoice[];
  quoteNumber?: string;
  quoteHref?: string;
  canView?: boolean;
  canEdit?: boolean;
  canCopyPublicLink?: boolean;
}>(), {
  quote: null,
  invoice: null,
  clientName: '',
  projectName: '',
  linkedInvoices: () => [],
  quoteNumber: '',
  quoteHref: '',
  canView: true,
  canEdit: false,
  canCopyPublicLink: false,
});

const emit = defineEmits<{
  'update:open': [value: boolean];
  view: [];
  edit: [];
  'copy-public-link': [];
  'view-linked-invoice': [invoice: Invoice];
}>();

const document = computed(() => props.kind === 'quote' ? props.quote : props.invoice);
const isQuote = computed(() => props.kind === 'quote');
const title = computed(() => document.value?.title || documentNumber.value || (isQuote.value ? 'Quote details' : 'Invoice details'));
const documentNumber = computed(() => isQuote.value ? props.quote?.quoteNumber : props.invoice?.invoiceNumber);
const currency = computed(() => document.value?.currency || 'USD');
const status = computed(() => document.value?.status || 'draft');
const lineItems = computed<(QuoteLineItem | InvoiceLineItem)[]>(() => document.value?.lineItems || []);
const deposit = computed(() => document.value?.deposit);
const paymentPlan = computed(() => document.value?.paymentPlan);
const issueDate = computed(() => document.value?.issueDate || 'Not set');
const endDateLabel = computed(() => isQuote.value ? 'Expires' : 'Due');
const endDate = computed(() => isQuote.value ? (props.quote?.expiryDate || 'Not set') : (props.invoice?.dueDate || 'Not set'));

const paidAmount = computed(() => {
  if (isQuote.value) {
    return props.linkedInvoices.reduce((sum, invoice) => {
      const paid = invoice.status === 'paid' ? invoice.amountTotal : invoice.amountPaid;
      return sum + Number(paid || 0);
    }, 0);
  }
  if (props.invoice?.status === 'paid') return Number(props.invoice.amountTotal || 0);
  return Number(props.invoice?.amountPaid || 0);
});

const outstandingAmount = computed(() => {
  if (isQuote.value) return Math.max(0, Number(props.quote?.amountTotal || 0) - paidAmount.value);
  return Math.max(0, Number(props.invoice?.amountDue || 0));
});

const stateHeadline = computed(() => {
  if (isQuote.value) {
    if (status.value === 'accepted') return props.quote && resolveQuoteAcceptanceSource(props.quote) === 'internal' ? 'Approved internally and ready to invoice' : 'Accepted by the client and ready to invoice';
    if (status.value === 'sent') return 'Waiting on client approval';
    if (status.value === 'rejected') return 'This quote was declined';
    if (status.value === 'expired') return 'This quote has expired';
    return props.quote?.clientFacing === false ? 'Internal draft' : 'Draft quote';
  }
  if (status.value === 'paid') return 'Fully paid';
  if (status.value === 'partial') return 'Partially paid and still open';
  if (status.value === 'overdue') return 'Overdue and needs attention';
  if (status.value === 'sent') return 'Sent and waiting for payment';
  return 'Internal draft';
});

const stateHint = computed(() => {
  if (isQuote.value) {
    if (status.value === 'accepted') return 'The commercial terms are approved. Review linked invoices or move into invoice creation.';
    if (status.value === 'sent') return 'The client can review this quote and respond using the shared document.';
    if (status.value === 'rejected') return 'Review the commercial terms before revising or creating a replacement quote.';
    if (status.value === 'expired') return 'Update the dates before sharing this quote again.';
    return props.quote?.clientFacing === false ? 'This quote is currently visible to the internal team only.' : 'Finish the pricing and send it when it is ready for review.';
  }
  if (status.value === 'paid') return 'The balance is settled and the final document remains available for reference.';
  if (status.value === 'partial') return 'A payment has been recorded, but an outstanding balance remains.';
  if (status.value === 'overdue') return 'The due date has passed, so a reminder or follow-up is the likely next action.';
  if (status.value === 'sent') return 'The invoice is with the client and waiting for payment.';
  return 'Review the invoice before sending it to the client.';
});

const statusDotClass = computed(() => {
  if (status.value === 'accepted' || status.value === 'paid') return 'bg-emerald-500';
  if (status.value === 'rejected' || status.value === 'expired' || status.value === 'overdue') return 'bg-destructive';
  return 'bg-muted-foreground/50';
});
const { downloadInvoicePdf, isDownloadingInvoice } = useInvoicePdfDownload();

async function downloadPdf() {
  if (!props.invoice) return;
  try {
    await downloadInvoicePdf(props.invoice);
    toast.success('Invoice PDF downloaded');
  } catch (error: any) {
    toast.error(error?.data?.statusMessage || error?.message || 'Invoice PDF could not be downloaded.');
  }
}

function lineAmount(item: QuoteLineItem | InvoiceLineItem): number {
  return Math.max(0, Number(item.quantity || 0) * Number(item.price || 0) - Number(item.discount || 0));
}
</script>

<template>
  <Sheet :open="open" @update:open="emit('update:open', $event)">
    <SheetContent side="right" class="!flex h-full !w-full max-w-none flex-col gap-0 overflow-hidden bg-background p-0 sm:!max-w-[800px]">
      <SheetHeader class="shrink-0 border-b border-border/70 px-6 py-5 pr-14 text-left">
        <div class="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{{ isQuote ? 'Quote' : 'Invoice' }}</Badge>
          <Badge variant="secondary" class="gap-1.5 capitalize"><span class="size-2 rounded-full" :class="statusDotClass" />{{ status }}</Badge>
          <Badge v-if="isQuote && quote" variant="outline">{{ quoteWasSharedWithClient(quote) ? 'Client-facing' : 'Internal only' }}</Badge>
        </div>
        <SheetTitle class="text-2xl font-semibold tracking-tight">{{ title }}</SheetTitle>
        <SheetDescription class="text-sm leading-6">
          {{ documentNumber || (isQuote ? 'No quote number' : 'No invoice number') }} · Issued {{ issueDate }} · {{ endDateLabel }} {{ endDate }}
        </SheetDescription>
      </SheetHeader>

      <div v-if="document" class="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <section class="grid gap-5 py-6 sm:grid-cols-4">
          <div><p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Total</p><p class="mt-1 text-sm font-semibold">{{ formatMoney(document.amountTotal, currency) }}</p></div>
          <div><p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Paid</p><p class="mt-1 text-sm font-semibold">{{ formatMoney(paidAmount, currency) }}</p></div>
          <div><p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Outstanding</p><p class="mt-1 text-sm font-semibold">{{ formatMoney(outstandingAmount, currency) }}</p></div>
          <div><p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{{ isQuote ? 'Invoices' : 'Line items' }}</p><p class="mt-1 text-sm font-semibold">{{ isQuote ? linkedInvoices.length : lineItems.length }}</p></div>
        </section>

        <section class="border-t border-border/70 py-6">
          <h3 class="text-sm font-semibold">{{ stateHeadline }}</h3>
          <p class="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">{{ stateHint }}</p>
          <dl class="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
            <div><dt class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Client</dt><dd class="mt-1 text-sm font-medium">{{ clientName || 'No relationship linked' }}</dd></div>
            <div><dt class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Project</dt><dd class="mt-1 text-sm font-medium">{{ projectName || 'Standalone document' }}</dd></div>
          </dl>
        </section>

        <section class="border-t border-border/70 py-6">
          <div class="flex items-center justify-between gap-3">
            <div><h3 class="text-sm font-semibold">Line items</h3><p class="mt-1 text-sm text-muted-foreground">The work and pricing included in this {{ isQuote ? 'quote' : 'invoice' }}.</p></div>
            <Badge variant="outline">{{ lineItems.length }}</Badge>
          </div>
          <div v-if="lineItems.length" class="mt-4 divide-y divide-border/60 border-y border-border/60">
            <div v-for="(item, index) in lineItems" :key="`${item.name}-${index}`" class="flex items-start justify-between gap-4 py-3.5">
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium">{{ item.name || `Item ${index + 1}` }}</p>
                <p v-if="item.description" class="mt-1 text-xs leading-5 text-muted-foreground">{{ item.description }}</p>
                <p class="mt-1 text-xs text-muted-foreground">{{ item.quantity }} × {{ formatMoney(item.price, currency) }}<span v-if="item.discount"> · {{ formatMoney(item.discount, currency) }} discount</span></p>
              </div>
              <p class="shrink-0 text-sm font-semibold">{{ formatMoney(lineAmount(item), currency) }}</p>
            </div>
          </div>
          <p v-else class="mt-4 border-y border-border/60 py-4 text-sm text-muted-foreground">No individual line items are recorded.</p>
        </section>

        <section class="border-t border-border/70 py-6">
          <h3 class="text-sm font-semibold">Payment structure</h3>
          <div class="mt-4 divide-y divide-border/60 border-y border-border/60 text-sm">
            <div class="flex items-center justify-between gap-4 py-3">
              <span class="text-muted-foreground">Deposit</span>
              <span class="text-right font-medium">{{ deposit?.enabled ? (deposit.mode === 'percentage' ? `${deposit.value}%` : formatMoney(deposit.value || 0, currency)) : 'Not required' }}</span>
            </div>
            <div class="flex items-center justify-between gap-4 py-3">
              <span class="text-muted-foreground">Payment plan</span>
              <span class="text-right font-medium">{{ paymentPlan?.enabled ? `${paymentPlan.installments?.length || 0} installments` : 'Single payment' }}</span>
            </div>
          </div>
        </section>

        <section v-if="!isQuote && (invoice?.stripePaymentIntentId || invoice?.stripeInvoiceNumber || invoice?.stripeInvoiceId)" class="border-t border-border/70 py-6">
          <div><h3 class="text-sm font-semibold">Payment references</h3><p class="mt-1 text-sm text-muted-foreground">Use these references when reconciling Vuey payments with Stripe.</p></div>
          <dl class="mt-4 divide-y divide-border/60 border-y border-border/60 text-sm">
            <div class="flex items-center justify-between gap-4 py-3"><dt class="text-muted-foreground">Vuey invoice</dt><dd class="text-right font-medium">{{ invoice.invoiceNumber || invoice.id }}</dd></div>
            <div v-if="invoice.stripePaymentIntentId" class="flex items-center justify-between gap-4 py-3"><dt class="text-muted-foreground">Latest Stripe payment</dt><dd class="break-all text-right font-medium">{{ invoice.stripePaymentIntentId }}</dd></div>
            <div v-if="invoice.stripeInvoiceId" class="flex items-center justify-between gap-4 py-3"><dt class="text-muted-foreground">Legacy Stripe invoice</dt><dd class="text-right font-medium">{{ invoice.stripeInvoiceNumber || invoice.stripeInvoiceId }}</dd></div>
          </dl>
        </section>

        <section class="border-t border-border/70 py-6">
          <div><h3 class="text-sm font-semibold">Linked records</h3><p class="mt-1 text-sm text-muted-foreground">Trace the commercial documents connected to this record.</p></div>
          <div v-if="isQuote && linkedInvoices.length" class="mt-4 divide-y divide-border/60 border-y border-border/60">
            <button v-for="linkedInvoice in linkedInvoices" :key="linkedInvoice.id" type="button" class="flex w-full items-center justify-between gap-4 py-3 text-left hover:text-primary" @click="emit('view-linked-invoice', linkedInvoice)">
              <span class="inline-flex min-w-0 items-center gap-2"><ReceiptText class="size-4 shrink-0 text-muted-foreground" /><span class="truncate text-sm font-medium">{{ linkedInvoice.title || linkedInvoice.invoiceNumber || linkedInvoice.id }}</span></span>
              <span class="shrink-0 text-sm text-muted-foreground">{{ formatMoney(linkedInvoice.amountTotal, linkedInvoice.currency || currency) }}</span>
            </button>
          </div>
          <div v-else-if="!isQuote && invoice?.quoteId" class="mt-4 border-y border-border/60 py-3">
            <a :href="quoteHref" class="inline-flex items-center gap-2 text-sm font-medium hover:text-primary"><FileText class="size-4 text-muted-foreground" />Source quote {{ quoteNumber || invoice.quoteId }}</a>
          </div>
          <p v-else class="mt-4 border-y border-border/60 py-4 text-sm text-muted-foreground">No linked commercial records yet.</p>
          <a v-if="!isQuote && invoice?.allowThirdPartyPayments !== false && invoice?.stripeCheckoutURL" :href="invoice.stripeCheckoutURL" target="_blank" rel="noopener noreferrer" class="mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary"><ExternalLink class="size-4" />Open hosted payment page</a>
        </section>
      </div>

      <SheetFooter class="shrink-0 border-t border-border/70 bg-background/95 px-6 py-4 backdrop-blur sm:flex-row sm:justify-between">
        <Button variant="outline" @click="emit('update:open', false)">Close</Button>
        <div class="flex flex-col-reverse gap-2 sm:flex-row">
          <Button v-if="!isQuote && invoice" variant="outline" :disabled="isDownloadingInvoice(invoice.id)" @click="downloadPdf">
            <LoaderCircle v-if="isDownloadingInvoice(invoice.id)" class="mr-2 size-4 animate-spin" />
            <Download v-else class="mr-2 size-4" />
            Download PDF
          </Button>
          <Button v-if="canCopyPublicLink" variant="outline" @click="emit('copy-public-link')"><Copy class="mr-2 size-4" />Copy public link</Button>
          <Button v-if="canEdit" variant="outline" @click="emit('edit')"><Pencil class="mr-2 size-4" />Edit</Button>
          <Button v-if="canView" @click="emit('view')"><ExternalLink class="mr-2 size-4" />Open {{ isQuote ? 'quote' : 'invoice' }}</Button>
        </div>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>
